import { useTranslation } from 'react-i18next';
import { Button, Tooltip } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import s from './TaskCell.module.scss';

import type { FC, MouseEvent } from 'react';
import type { TabKey } from 'pages/TasksDashboard/types';

interface ITaskCellProps {
  title: string;
  quickNotes: string | null;
  returnedAt: string | null;
  status: string;
  reportedByGuid: string;
  currentUserGuid: string;
  activeTab: TabKey;
  onEdit?: () => void;
  isBusy?: boolean;
}

const TaskCell: FC<ITaskCellProps> = ({
  title,
  quickNotes,
  returnedAt,
  status,
  reportedByGuid,
  currentUserGuid,
  activeTab,
  onEdit,
  isBusy,
}) => {
  const { t } = useTranslation();
  const showReturnPreview = status === 'unassigned' && !!quickNotes;
  const showEditIcon =
    activeTab === 'open_pool' && reportedByGuid === currentUserGuid;

  return (
    <div className={s['task-cell']} data-cy="task-cell">
      <div className={s['task-title-row']}>
        <Tooltip title={title}>
          <span className={s['task-title']}>{title}</span>
        </Tooltip>
        {showEditIcon && (
          <Tooltip title={t('tasksDashboard.actions.edit')}>
            <Button
              type="text"
              size="small"
              className={s['edit-icon']}
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                onEdit?.();
              }}
              disabled={isBusy}
              aria-label={t('tasksDashboard.actions.edit')}
              data-cy="edit-task-icon"
              icon={<EditOutlined />}
            />
          </Tooltip>
        )}
      </div>
      {showReturnPreview && (
        <div className={s['return-preview']}>
          <span>
            <span className={s['return-label']}>
              {t('tasksDashboard.returnPreview')}:
            </span>{' '}
            <span className={s['return-note-text']}>
              &ldquo;{quickNotes}&rdquo;
            </span>
          </span>
          {(() => {
            if (!returnedAt) return null;
            const parsed = dayjs(returnedAt);
            if (!parsed.isValid()) return null;
            return (
              <span className={s['return-timestamp']}>
                {parsed.format('MM/DD/YYYY, h:mm A')}
              </span>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default TaskCell;
